import apiReminderSetup from "../../api";
import { DATA_TYPE_LABEL, FIELD_ROLE_LABEL } from "../../../features/builder.constants";
import { COMPUTE_OP_LABEL, FORMAT_LABEL } from "./source-field.constants";

const ORDER_TYPES = ["INT", "NUMERIC", "DATE", "TIMESTAMP"];
const COMPUTED_ROLES = ["ATTRIBUTE", "USER_REF", "EMAIL_REF"];
const schemaOf = (res) => (res.status === "fulfilled" ? res.value?.data?.data ?? null : null);
const toList = (labels, keys = Object.keys(labels)) => keys.map((id) => ({ id, label: labels[id] }));

/** "a.b" -> ["a", "a.b"]: a listed relation and every prefix of it can be read. */
function readableRelationPaths(relations) {
  const out = new Set();
  for (const relation of relations ?? []) {
    const parts = relation.split(".");
    parts.forEach((_, index) => out.add(parts.slice(0, index + 1).join(".")));
  }
  return [...out].sort();
}

/**
 * Dropdown data of the computed field form for one source: every single-value column it may read (root model and
 * the to-one relations it lists), the root model's list relations (count / newest value) and their columns.
 */
export const makeComputedFieldLoader = (source) => async () => {
  const relationPaths = readableRelationPaths(source?.relations);
  const [rootRes, ...relationRes] = await Promise.allSettled([
    apiReminderSetup.getModelSchema(source.root_model, ""),
    ...relationPaths.map((path) => apiReminderSetup.getModelSchema(source.root_model, path)),
  ]);
  const root = schemaOf(rootRes);
  const listRelations = (root?.relations ?? []).filter((relation) => relation.list);
  const itemRes = await Promise.allSettled(listRelations.map((relation) => apiReminderSetup.getModelSchema(source.root_model, relation.name)));

  // A calculation reads single values, so columns reached through a list relation are left out.
  const singleSchemas = [root, ...relationRes.map(schemaOf)].filter((schema) => schema && !schema.list);
  const columns = singleSchemas.flatMap((schema) => (schema.columns ?? []).map((column) => ({ ...column, model_label: schema.label })));
  const columnOption = (column) => ({ id: column.path, label: `${column.path} · ${DATA_TYPE_LABEL[column.data_type] ?? column.data_type}` });
  const dateColumns = columns.filter((column) => ["DATE", "TIMESTAMP"].includes(column.data_type)).map(columnOption);

  const latestColumns = [];
  listRelations.forEach((relation, index) => {
    for (const column of schemaOf(itemRes[index])?.item_columns ?? []) {
      latestColumns.push({
        id: `${relation.name}.${column.column}`,
        label: `${relation.label} › ${column.column} · ${DATA_TYPE_LABEL[column.data_type] ?? column.data_type}`,
        data_type: column.data_type,
      });
    }
  });

  return {
    ops: toList(COMPUTE_OP_LABEL),
    amountModes: [
      { id: "fixed", label: "A fixed number" },
      { id: "field", label: "A number column" },
    ],
    dateColumns,
    fromColumns: [{ id: "today", label: "Today" }, ...dateColumns],
    numberColumns: columns.filter((column) => ["INT", "NUMERIC"].includes(column.data_type)).map(columnOption),
    listRelations: listRelations.map((relation) => ({ id: relation.name, label: `${relation.label} (${relation.name})` })),
    latestColumns,
    latestOrderColumns: latestColumns.filter((column) => ORDER_TYPES.includes(column.data_type)),
    roles: toList(FIELD_ROLE_LABEL, COMPUTED_ROLES),
    formats: toList(FORMAT_LABEL),
  };
};
